'use client';
import { useGetOrganization } from '@/config/sales/organization/client';
import { useFeatureFlag } from '@/hooks/use-feature-flag';
import { FEATURE_FLAGS } from '@/hooks/use-feature-flag/constants';
import { useSession } from '@/providers/session-provider';
import Script from 'next/script';
import { useEffect } from 'react';

declare global {
  interface Window {
    hsConversationsSettings?: { loadImmediately?: boolean };
    hsConversationsOnReady?: Array<() => void>;
    _hsq?: Array<[string, ...unknown[]]>;
    HubSpotConversations?: {
      on(
        eventName: 'unreadConversationCountChanged',
        handler: (payload: { unreadCount: number }) => void
      ): void;
      on(eventName: 'widgetClosed', handler: () => void): void;
      on(eventName: 'widgetLoaded', handler: () => void): void;
      off(
        eventName: 'unreadConversationCountChanged',
        handler: (payload: { unreadCount: number }) => void
      ): void;
      off(eventName: 'widgetClosed', handler: () => void): void;
      off(eventName: 'widgetLoaded', handler: () => void): void;
      widget: {
        load: () => void;
        open: () => void;
        status: () => { loaded: boolean };
      };
    };
  }
}

// How long to wait for the Conversations API to attach before treating the
// chat as unavailable (e.g. no chatflow targets the page, or it's blocked).
const CHAT_UNAVAILABLE_TIMEOUT_MS = 3000;

// Fallback delay for opening the widget after `widget.load()` — the
// `widgetLoaded` event is flaky, so we open after this timeout if it hasn't
// fired yet.
const WIDGET_OPEN_FALLBACK_TIMEOUT_MS = 3000;

// We keep the widget loaded for the whole session (so re-opening is instant)
// and hide HubSpot's launcher/panel container via CSS when the chat is closed,
// rather than calling `widget.remove()` (which would force a fresh, flaky load
// on every open). This class is toggled on <body>; see globals.css.
const HS_CHAT_HIDDEN_CLASS = 'hs-chat-hidden';

function setHubSpotChatVisible(visible: boolean) {
  document.body.classList.toggle(HS_CHAT_HIDDEN_CLASS, !visible);
}

// Guards against stacking listeners/timers when the button is clicked several
// times while the first `load()` is still in flight.
let widgetLoadPending = false;

// Opens the HubSpot chat widget. The embed is initialized with
// `loadImmediately: false` (so HubSpot's default launcher never renders), so on
// the first open we load the widget and open it once `widgetLoaded` fires (or
// after a fallback timeout, since that event is flaky); once loaded it stays
// loaded, so later opens are an instant `open()`. If the Conversations API
// hasn't attached yet (the embed is still initializing), the open is queued via
// `hsConversationsOnReady` so an early click isn't lost — and if it still hasn't
// attached after a short timeout, `onUnavailable` is called so the caller can
// surface feedback.
//
// Note: we intentionally do NOT call `widget.refresh()` on route changes — per
// HubSpot's docs, refreshing on a route the chatflow doesn't target removes the
// widget. The widget persists across client-side navigation on its own.
export function openHubSpotChat(onUnavailable?: () => void) {
  const openWidget = () => {
    const conversations = window.HubSpotConversations;
    if (!conversations) {
      return false;
    }
    // Reveal the container (hidden by default / after close) before opening.
    setHubSpotChatVisible(true);

    if (conversations.widget.status().loaded) {
      conversations.widget.open();
      return true;
    }

    if (widgetLoadPending) {
      return true;
    }
    widgetLoadPending = true;

    // `load()` is async, so open the widget once it has loaded. The
    // `widgetLoaded` event is flaky, so also fall back to a timeout —
    // whichever fires first opens the widget and cancels the other.
    let opened = false;
    let fallbackTimer: ReturnType<typeof setTimeout>;
    const openOnce = () => {
      if (opened) return;
      opened = true;
      widgetLoadPending = false;
      clearTimeout(fallbackTimer);
      conversations.off('widgetLoaded', openOnce);
      conversations.widget.open();
    };
    conversations.on('widgetLoaded', openOnce);
    fallbackTimer = setTimeout(openOnce, WIDGET_OPEN_FALLBACK_TIMEOUT_MS);
    conversations.widget.load();
    return true;
  };

  if (openWidget()) {
    return;
  }

  let opened = false;
  window.hsConversationsOnReady = window.hsConversationsOnReady ?? [];
  window.hsConversationsOnReady.push(() => {
    opened = openWidget();
  });

  setTimeout(() => {
    if (!opened) {
      onUnavailable?.();
    }
  }, CHAT_UNAVAILABLE_TIMEOUT_MS);
}

// Subscribes to HubSpot's unread conversation count and returns a cleanup
// function. Defers the subscription via `hsConversationsOnReady` when the
// Conversations API hasn't attached yet.
export function subscribeToHubSpotUnreadCount(
  onChange: (unreadCount: number) => void
) {
  const handler = (payload: { unreadCount: number }) => {
    onChange(payload.unreadCount);
  };
  const register = () => {
    window.HubSpotConversations?.on('unreadConversationCountChanged', handler);
  };

  if (window.HubSpotConversations) {
    register();
  } else {
    window.hsConversationsOnReady = window.hsConversationsOnReady ?? [];
    window.hsConversationsOnReady.push(register);
  }

  return () => {
    window.HubSpotConversations?.off('unreadConversationCountChanged', handler);
  };
}

// Hides the widget (launcher + panel) when the user closes the chat, so it's
// only ever shown via our button — without unloading it, so re-opening stays
// instant. Returns a cleanup function. Deferred via `hsConversationsOnReady`
// when the Conversations API hasn't attached yet.
export function hideHubSpotChatOnClose() {
  const handleClose = () => {
    setHubSpotChatVisible(false);
  };
  const register = () => {
    window.HubSpotConversations?.on('widgetClosed', handleClose);
  };

  if (window.HubSpotConversations) {
    register();
  } else {
    window.hsConversationsOnReady = window.hsConversationsOnReady ?? [];
    window.hsConversationsOnReady.push(register);
  }

  return () => {
    window.HubSpotConversations?.off('widgetClosed', handleClose);
  };
}

// Eagerly loads the widget (hidden via CSS) so the first open is instant rather
// than waiting on an async `load()`. Deferred via `hsConversationsOnReady` when
// the Conversations API hasn't attached yet.
export function loadHubSpotWidget() {
  const load = () => {
    const widget = window.HubSpotConversations?.widget;
    if (widget && !widget.status().loaded) {
      widget.load();
    }
  };

  if (window.HubSpotConversations) {
    load();
  } else {
    window.hsConversationsOnReady = window.hsConversationsOnReady ?? [];
    window.hsConversationsOnReady.push(load);
  }
}

// Sent once per page load to flush the queued `identify` (see below).
let hasTrackedPageView = false;

// Associates the current visitor with a HubSpot contact via the tracking code's
// `identify` call, so the chat knows who they are (name/email/company
// pre-filled, email capture skipped). This is the lightweight, *unverified*
// path — fine for known logged-in users, but it does not cryptographically
// prove identity (use the Visitor Identification token flow for that). The
// identify is flushed by a single `trackPageView`; later calls (e.g. once the
// company name resolves) update the contact and ride along with later events.
export function identifyHubSpotVisitor(info: {
  email: string;
  firstName?: string;
  lastName?: string;
  company?: string;
}) {
  const hsq = (window._hsq = window._hsq ?? []);
  hsq.push([
    'identify',
    {
      email: info.email,
      firstname: info.firstName,
      lastname: info.lastName,
      company: info.company,
    },
  ]);
  if (!hasTrackedPageView) {
    hsq.push(['trackPageView']);
    hasTrackedPageView = true;
  }
}

// Loads the HubSpot embed, gated to the CX demo tenant via the HUBSPOT_CHAT
// feature flag. The widget is opened from our own button (see `openHubSpotChat`)
// — its default launcher is suppressed via `loadImmediately: false` (set in the
// root layout before this loader runs). We then eagerly load it hidden (via the
// `hs-chat-hidden` body class) so the first open is instant, and keep it hidden
// between opens.
export default function HubSpotChatLoader() {
  const enabled = useFeatureFlag(FEATURE_FLAGS.HUBSPOT_CHAT);
  const {
    data: {
      user,
      session: { organizationId },
    },
  } = useSession();
  const { data: organization } = useGetOrganization({
    id: organizationId,
    enabled: enabled && !!organizationId,
  });

  useEffect(() => {
    if (!enabled) return;
    // Hide first (before the widget loads) so the launcher never flashes, then
    // load it eagerly so the first open is instant.
    setHubSpotChatVisible(false);
    loadHubSpotWidget();
    return hideHubSpotChatOnClose();
  }, [enabled]);

  useEffect(() => {
    if (!enabled || !user?.email) return;
    identifyHubSpotVisitor({
      email: user.email,
      firstName: user.firstName,
      lastName: user.lastName,
      company: organization?.name,
    });
  }, [
    enabled,
    user?.email,
    user?.firstName,
    user?.lastName,
    organization?.name,
  ]);

  if (!enabled) {
    return null;
  }

  return (
    <Script
      id='hs-script-loader'
      src='//js-na1.hs-scripts.com/48043996.js'
      strategy='afterInteractive'
    />
  );
}
